import { Box, Stack } from "@mui/material";
import { ContentLayout, HeaderBreadcrumbs } from "../../../shared";
import { DashboardChart, DashboardChartLoader } from "../../Dashboard";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import BarChartIcon from "@mui/icons-material/BarChart";
import { usePLDashboardList } from "../hooks";
import React from "react";
import { PlAndBsDashboardFilterSection } from "../components";
import { LoadingButton } from "@mui/lab";

const labels = [
  "COGS (%)",
  "Total Wage Ratio (%)",
  "All Leave Costs (%)",
  "In Store Ratio (%)",
  "Direct Costs (%)",
  "Overheads (%)",
  "EBITDA (%)",
  "NPAT (Net Profit after tax) (%)",
  "RIFTY (Retained Income for the Year) (%)",
];

export const PLDashboardList = () => {
  const [selected, setSelected] = React.useState("horizontal");
  const {
    validator,
    state,
    isStoreAdmin,
    handleChangeFilter,
    handleApplyFilter,
    getChartTitle,
  } = usePLDashboardList();

  const handleSelection = (event, newSelection) => {
    if (newSelection !== null) {
      setSelected(newSelection);
    }
  };

  return (
    <ContentLayout
      title="PL Dashboard"
      breadcrumbs={
        <HeaderBreadcrumbs
          links={[{ name: "Dashboard", href: "/" }, { name: "PL Dashboard" }]}
        />
      }
      rightContent={
        <ToggleButtonGroup
          value={selected}
          exclusive
          onChange={handleSelection}
          sx={{
            border: "1px solid #1976d2",
            width: "112px",
            borderRadius: "8px",
            overflow: "hidden",
            "& .MuiToggleButton-root": {
              color: "#1976d2",
              flex: 1,
              border: "none",
              borderRadius: "0",
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "rgba(21, 96, 212, 1)",
                },
              },
              "&:hover": {
                backgroundColor: "rgba(21, 96, 212, 0.2)",
              },
            },
          }}
        >
          <ToggleButton value="horizontal">
            <FormatAlignLeftIcon />
          </ToggleButton>
          <ToggleButton value="vertical">
            <BarChartIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      }
      topContent={
        <Stack
          width="100%"
          gap="8px"
          flexWrap="wrap"
          direction="row"
          justifyContent="space-between"
        >
          <Stack
            flexDirection="row"
            gap="16px"
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="start"
          >
            <Stack flexDirection="row" gap="16px" flexWrap="wrap">
              <PlAndBsDashboardFilterSection
                handleChangeFilter={handleChangeFilter}
                state={state}
                storeList={!isStoreAdmin}
                stateList={!isStoreAdmin}
                validator={validator}
              />
            </Stack>
            <LoadingButton
              variant="contained"
              onClick={handleApplyFilter}
              disabled={state.filter.isFilterApplied}
              sx={{ ml: "auto" }}
              loading={state?.chartLoading}
            >
              Apply
            </LoadingButton>
          </Stack>
        </Stack>
      }
    >
      <Stack
        direction={selected === "horizontal" ? "column" : "row"}
        flexWrap="wrap"
        gap={2}
      >
        {state.chartLoading
          ? Array.from({ length: 9 }).map((_, index) => {
              return (
                <DashboardChartLoader
                  key={index}
                  title={labels[index]}
                  horizontal={selected === "horizontal"}
                  cardMinHeight="300px"
                  barHeight="30px"
                />
              );
            })
          : Object.entries(state.charts).map(([key, series], index) => {
              const title = getChartTitle(key);
              const isHorizontal = selected === "horizontal";
              const maxValue = Math.max(...series.y_axis);
              const minValue = Math.min(...series.y_axis);

              return (
                <Box key={key} sx={{ flex: "1 1 calc(33% - 16px)" }}>
                  <DashboardChart
                    index={index}
                    title={title}
                    data={series}
                    xAxisKey="x_axis"
                    yAxisKey="y_axis"
                    barHeight="35px"
                    barColor={isHorizontal ? "#564845" : "#A04D3D"}
                    horizontal={isHorizontal}
                    seriesName={title}
                    valueFormatter={(value) => value?.toFixed(2) + "%"}
                    xAxisLabelFormatter={(label) =>
                      isHorizontal ? label + "%" : label
                    }
                    yAxisOption={
                      maxValue <= 5 && minValue >= 0
                        ? {
                            min: 0,
                            max: 5,
                          }
                        : undefined
                    }
                  />
                </Box>
              );
            })}
      </Stack>
    </ContentLayout>
  );
};
